import * as vp from 'vopay';
import { FunctionComponent, JSX } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const { Table, Box, SubTitle, Badge, formattedCurrency } = vp;

type TransactionInfoProps = {
  transaction: vp.RTPWithdrawTransactionResponse;
  Notes: string;
};

type DataProps = {
  hasErrorInfo: boolean;
  failureReason: string;
};

type FC = FunctionComponent<TransactionInfoProps>;

type KeyValuePair = {
  display: string;
  value: string | JSX.Element;
  visible?: boolean;
};

type TablePropsHandlers = {
  transactionDetail: () => vp.TableProps;
  accountHolderDetails: () => vp.TableProps;
  errorInfo: () => vp.TableProps;
};

const RTPWithdrawalInfo: FC = (props: TransactionInfoProps) => {
  const { state } = useLocation<DataProps>();
  const { transaction, Notes } = props;

  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.RTPWithdrawalInfo',
  });
  const { t: tGlobal } = useTranslation('translations');
  return (
    <>
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('transactionDetailsHeader') || ''} />
      </Box>
      <Table {...getTableProps('transactionDetail')} />
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('accountHolderDetailsHeader') || ''} />
      </Box>
      <Table {...getTableProps('accountHolderDetails')} />

      {state?.hasErrorInfo && (
        <>
          <Box mt="lg" pt="md" mb="md">
            <SubTitle subTitle={t('errorInfoHeader') || ''} />
          </Box>
          <Table {...getTableProps('errorInfo')} />
        </>
      )}
    </>
  );

  function getTableProps(handler: keyof TablePropsHandlers): vp.TableProps {
    const handlers: TablePropsHandlers = {
      transactionDetail: () => {
        const transactionDetailsPairs: KeyValuePair[] = [
          {
            display: t('transactionDetails.transactionID'),
            value: transaction.TransactionID,
          },
          {
            display: t('transactionDetails.transactionStatus'),
            value: (
              <Badge
                label={vp.capitalizeFirstLetter(
                  tGlobal(`Transaction.TransactionHistory.statuses.${transaction.TransactionStatus}`, {
                    defaultValue: transaction.TransactionStatus,
                  }),
                )}
                hasIcon
                color={vp.GetColorStatus(transaction.TransactionStatus)}
              />
            ),
          },
          {
            display: t('transactionDetails.transactionDate'),
            value: transaction.TransactionDateTime,
          },
          {
            display: t('transactionDetails.lastModified'),
            value: transaction.LastModified,
          },
          {
            display: t('transactionDetails.type'),
            value:
              tGlobal('Transaction.TransactionHistory.transactionTypes.RTP Withdraw', {
                defaultValue: 'RTP Withdraw',
              }) || '',
          },
          {
            display: t('transactionDetails.amount'),
            value: `$${formattedCurrency(transaction.Amount)}`,
          },
          {
            display: t('transactionDetails.currency'),
            value: transaction.Currency,
          },
          {
            display: t('transactionDetails.notes'),
            value: Notes,
          },
        ];

        const transactionDetailsProps: vp.TableProps = {
          name: 'transaction-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('transactionDetailsHeader') || '' },
            detailValue: { header: '' },
          },
          rows: transactionDetailsPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return transactionDetailsProps;
      },

      accountHolderDetails: () => {
        const accountHolderDetailsPairs: KeyValuePair[] = [
          {
            display: t('accountHolderDetails.name'),
            value: `${transaction.RecipientName}`,
            visible: !!transaction.RecipientName,
          },
          {
            display: t('accountHolderDetails.abaRoutingNumber'),
            value: `${transaction.ABARoutingNumber}`,
            visible: transaction.ABARoutingNumber !== '',
          },
          {
            display: t('accountHolderDetails.accountNumber'),
            value: transaction.AccountNumber,
            visible: !!transaction.AccountNumber,
          },
        ];

        const accountHolderDetailsProps: vp.TableProps = {
          name: 'account-holder-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('accountHolderDetailsHeader') || '' },
            detailValue: { header: '' },
          },
          rows: accountHolderDetailsPairs
            .filter(({ visible }) => visible)
            .map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })),
        };

        return accountHolderDetailsProps;
      },
      errorInfo: () => {
        const ErrorInfoPairs: KeyValuePair[] = [
          {
            display: t('errorInfo.failureDateTime'),
            value: transaction.LastModified,
          },
          {
            display: t('errorInfo.amount'),
            value: `$${formattedCurrency(transaction.Amount || '')}`,
          },
          {
            display: t('errorInfo.failureReason'),
            value: state.failureReason,
          },
        ];

        const ErrorInfoProps: vp.TableProps = {
          name: 'error-info',
          variant: 'pair',
          columns: {
            detailKey: { header: t('errorInfoHeader') || '' },
            detailValue: { header: '' },
          },
          rows: ErrorInfoPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return ErrorInfoProps;
      },
    };

    return handlers[handler]();
  }
};

export default RTPWithdrawalInfo;
